import { useEffect, useMemo, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import {
  VizCard,
  NowYouTry,
  VIZ_BTN_GHOST,
  usePrefersReducedMotion,
  useMountSequence,
  cueIsActive,
  cueHighlights,
  type VizCue,
} from '../vizShared';

// EnergyProfile — a reaction energy diagram (reaction coordinate vs potential
// energy). A smooth curve climbs from the reactants over an activation-energy
// hump (the transition state) and settles to the products — lower for an
// exothermic reaction (energy released, ΔH < 0) or higher for an endothermic one
// (energy absorbed, ΔH > 0). Annotates Ea (reactants → peak) and ΔH (reactants →
// products), and can overlay a catalysed path with a lowered hump.
//
// config: { type?: 'exothermic'|'endothermic', ea?, deltaH?, reactantLabel?,
//           productLabel?, catalyst?, title? }
// Cue: cue.step walks reactants → transition state → products (spotlighting each
// region); cue.highlight ∈ {ea, activation, deltah, enthalpy, reactants,
// products, catalyst} spotlights a label; cue.progress traces a marker along the
// curve. Resilient: bad/empty numbers fall back to sensible defaults.

export interface EnergyProfileConfig {
  type?: 'exothermic' | 'endothermic';
  /** Activation energy (peak above reactants), in kJ/mol. */
  ea?: number;
  /** Enthalpy change, kJ/mol. Sign is normalized to match `type` when given. */
  deltaH?: number;
  reactantLabel?: string;
  productLabel?: string;
  /** Show a catalysed path with a lowered activation hump. */
  catalyst?: boolean;
  title?: string;
}

interface Props {
  config?: EnergyProfileConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

// SVG plot geometry (matches the house style used by TitrationCurve et al.).
const PLOT_W = 340;
const PLOT_H = 230;
const PAD_L = 30;
const PAD_R = 16;
const PAD_T = 18;
const PAD_B = 30;

// Reaction-coordinate fractions (0..1 across the x-axis) for the key features.
const X_REACT = 0.12; // reactants plateau (right edge of it)
const X_PEAK = 0.5; // transition-state summit
const X_PROD = 0.88; // products plateau (left edge of it)

type StepKey = 'reactants' | 'activation' | 'products';
const STEP_ORDER: StepKey[] = ['reactants', 'activation', 'products'];

function num(n: unknown, fallback: number): number {
  const v = typeof n === 'number' ? n : NaN;
  return Number.isFinite(v) ? v : fallback;
}

export default function EnergyProfile({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);

  // ----- Resolve + sanitize the model ---------------------------------------
  const type: 'exothermic' | 'endothermic' =
    config.type === 'endothermic' ? 'endothermic' : 'exothermic';
  const exo = type === 'exothermic';

  // Energies are relative units (kJ/mol). Reactants sit at 0 as the baseline.
  const ea = Math.max(5, num(config.ea, exo ? 50 : 40));
  // ΔH: products relative to reactants. Normalize the sign to the chosen type
  // (negative when exothermic, positive when endothermic) using its magnitude.
  const dhRaw = num(config.deltaH, exo ? -30 : 35);
  const dhMag = Math.abs(dhRaw) < 1e-6 ? (exo ? 30 : 35) : Math.abs(dhRaw);
  // For endothermic, ΔH cannot exceed Ea (products can't sit above the peak).
  const deltaH = exo ? -dhMag : Math.min(dhMag, ea - 5);

  const reactantE = 0;
  const peakE = ea; // peak above reactants
  const productE = deltaH; // signed: below (exo) or above (endo) reactants
  const catEa = Math.max(5, ea * 0.5); // catalysed activation energy
  const catPeakE = catEa;
  const showCatalyst = Boolean(config.catalyst);

  const reactantLabel = config.reactantLabel ?? 'Reactants';
  const productLabel = config.productLabel ?? 'Products';

  // Energy axis range with a little headroom below and above.
  const eMin = Math.min(reactantE, productE) - 12;
  const eMax = Math.max(peakE, productE) + 16;

  // ----- Coordinate mappers --------------------------------------------------
  const sx = (fr: number) => PAD_L + fr * (PLOT_W - PAD_L - PAD_R);
  const sy = (e: number) =>
    PAD_T + (1 - (e - eMin) / (eMax - eMin)) * (PLOT_H - PAD_T - PAD_B);

  // Build a smooth path (reactant plateau → rise over peak → product plateau)
  // by sampling a piecewise function so the marker can ride the same curve.
  const energyAt = useMemo(() => {
    return (fr: number, peak: number, prodPlateauE: number): number => {
      if (fr <= X_REACT) return reactantE;
      if (fr >= X_PROD) return prodPlateauE;
      if (fr <= X_PEAK) {
        // smoothstep reactants → peak
        const t = (fr - X_REACT) / (X_PEAK - X_REACT);
        const s = t * t * (3 - 2 * t);
        return reactantE + (peak - reactantE) * s;
      }
      // smoothstep peak → products
      const t = (fr - X_PEAK) / (X_PROD - X_PEAK);
      const s = t * t * (3 - 2 * t);
      return peak + (prodPlateauE - peak) * s;
    };
  }, [reactantE]);

  const SAMPLES = 80;
  const pathFor = (peak: number, prodPlateauE: number) => {
    let d = '';
    for (let i = 0; i <= SAMPLES; i++) {
      const fr = i / SAMPLES;
      const x = sx(fr);
      const y = sy(energyAt(fr, peak, prodPlateauE));
      d += `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)} `;
    }
    return d.trim();
  };

  const mainPath = useMemo(
    () => pathFor(peakE, productE),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [peakE, productE, eMin, eMax],
  );
  const catPath = useMemo(
    () => pathFor(catPeakE, productE),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catPeakE, productE, eMin, eMax],
  );

  // ----- Interaction / reveal state -----------------------------------------
  // -1 = nothing spotlighted (whole curve neutral); 0..2 = step focus.
  const [step, setStep] = useState<number>(hasCue ? 0 : -1);
  const [marker, setMarker] = useState<number | null>(null); // fr along curve, cue.progress
  const interactedRef = useRef(false);

  const mark = (result?: unknown) => {
    if (!interactedRef.current) {
      interactedRef.current = true;
      onComplete?.(result ?? { type: 'energy_profile', reaction: type });
    }
  };

  // Self-demo: walk reactants → activation → products once on mount.
  const demo = useMountSequence(
    [
      { at: 350, run: () => setStep(0) },
      { at: 1150, run: () => setStep(1) },
      { at: 1950, run: () => setStep(2) },
      { at: 2750, run: () => setStep(-1) },
    ],
    { skip: reduced || hasCue, onSettle: () => setStep(-1) },
  );

  const cancelDemo = () => {
    if (demo.running) demo.cancel();
  };

  // ----- Cue handling --------------------------------------------------------
  useEffect(() => {
    if (cue?.step == null) return;
    const idx = Math.max(0, Math.min(STEP_ORDER.length - 1, Math.round(cue.step)));
    setStep(idx);
    setMarker(null);
  }, [cue?.step]);

  useEffect(() => {
    if (cue?.progress == null) return;
    setMarker(Math.max(0, Math.min(1, cue.progress)));
  }, [cue?.progress]);

  // highlight tokens map onto a label/region spotlight.
  const highlighted = useMemo(() => {
    const h = cueHighlights(cue);
    const set = new Set<string>();
    for (const t of h) {
      if (/^(ea|activation|barrier|transition)/.test(t)) set.add('ea');
      if (/^(delta|enthalp|heat)/.test(t) || t.includes('δh') || t.includes('dh')) set.add('deltaH');
      if (t.includes('react')) set.add('reactants');
      if (t.includes('prod')) set.add('products');
      if (t.includes('cataly')) set.add('catalyst');
    }
    return set;
  }, [cue]);

  // Resolve which region/labels are spotlighted (step OR highlight).
  const focus = {
    reactants: step === 0 || highlighted.has('reactants'),
    activation: step === 1 || highlighted.has('ea'),
    products: step === 2 || highlighted.has('products'),
    ea: step === 1 || highlighted.has('ea'),
    deltaH: step === 2 || highlighted.has('deltaH'),
    catalyst: highlighted.has('catalyst'),
  };
  const anyFocus = step >= 0 || highlighted.size > 0;

  // ----- Derived plot coordinates -------------------------------------------
  const reactPt = { x: sx(X_REACT), y: sy(reactantE) };
  const peakPt = { x: sx(X_PEAK), y: sy(peakE) };
  const prodPt = { x: sx(X_PROD), y: sy(productE) };
  const catPeakPt = { x: sx(X_PEAK), y: sy(catPeakE) };

  // Ea bracket: vertical from reactant level up to the peak, at the peak's x.
  const eaX = peakPt.x;
  // ΔH bracket: vertical from reactant level to product level, near the products.
  const dhX = sx(X_PROD + 0.06);

  // Marker position when cue.progress is driving.
  const markerPt =
    marker == null
      ? null
      : { x: sx(marker), y: sy(energyAt(marker, peakE, productE)) };

  const stepNote: Record<number, string> = {
    [-1]: exo
      ? 'Products sit below the reactants — energy is released (exothermic, ΔH < 0).'
      : 'Products sit above the reactants — energy is absorbed (endothermic, ΔH > 0).',
    0: 'Reactants start at the baseline energy, before any bonds break.',
    1: 'At the transition state, energy peaks. The climb from reactants is the activation energy, Ea.',
    2: exo
      ? 'Bonds re-form and the system drops to the products — net energy released (ΔH < 0).'
      : 'The system settles above the reactants — net energy absorbed (ΔH > 0).',
  };

  const dimmed = (active: boolean) => (anyFocus && !active ? 0.28 : 1);

  return (
    <VizCard
      eyebrow="Thermochemistry · Reaction energy"
      title={config.title ?? `Reaction energy profile — ${exo ? 'exothermic' : 'endothermic'}`}
      subtitle={`${reactantLabel} → ${productLabel}`}
      reducedMotion={reduced}
      hint="Click a stage (reactants · transition state · products) to step through · the dashed path shows a catalyst lowering Ea"
      actions={
        <button
          type="button"
          onClick={() => {
            cancelDemo();
            setStep((s) => (s + 1) % 3);
            setMarker(null);
            mark();
          }}
          className={VIZ_BTN_GHOST}
        >
          <RotateCcw size={12} /> Step
        </button>
      }
    >
      <div className="relative w-full select-none">
        <NowYouTry show={demo.done && !reduced && !hasCue} />

        <svg
          viewBox={`0 0 ${PLOT_W} ${PLOT_H}`}
          className="w-full rounded-md border border-ws-border bg-ws-surface-2"
          role="img"
          aria-label={`${type} reaction energy diagram from ${reactantLabel} to ${productLabel}`}
        >
          {/* Axes */}
          <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={PLOT_H - PAD_B} stroke="var(--ws-border-strong)" strokeWidth="1" />
          <line x1={PAD_L} y1={PLOT_H - PAD_B} x2={PLOT_W - PAD_R} y2={PLOT_H - PAD_B} stroke="var(--ws-border-strong)" strokeWidth="1" />
          <text x={PAD_L - 4} y={PAD_T + 4} textAnchor="end" fontSize="7.5" fill="var(--ws-ink-3)" transform={`rotate(-90 ${PAD_L - 18} ${PLOT_H / 2})`}>
            Potential energy
          </text>
          <text x={(PLOT_W + PAD_L) / 2} y={PLOT_H - 4} textAnchor="middle" fontSize="8" fill="var(--ws-ink-3)">
            Reaction progress →
          </text>

          {/* Reference dashed levels for reactants & products (to read Ea / ΔH) */}
          <line x1={reactPt.x} y1={reactPt.y} x2={dhX + 4} y2={reactPt.y} stroke="var(--ws-ink-4)" strokeWidth="0.75" strokeDasharray="3 3" opacity="0.7" />
          <line x1={X_PEAK < X_PROD ? sx(X_PEAK) : reactPt.x} y1={peakPt.y} x2={eaX} y2={peakPt.y} stroke="var(--ws-ink-4)" strokeWidth="0.75" strokeDasharray="3 3" opacity="0.6" />
          <line x1={prodPt.x} y1={prodPt.y} x2={dhX + 4} y2={prodPt.y} stroke="var(--ws-ink-4)" strokeWidth="0.75" strokeDasharray="3 3" opacity="0.7" />

          {/* Catalysed path (lower hump) */}
          {showCatalyst && (
            <path
              d={catPath}
              fill="none"
              stroke="var(--ws-success)"
              strokeWidth="1.75"
              strokeDasharray="5 4"
              strokeLinecap="round"
              opacity={dimmed(focus.catalyst)}
              style={{ transition: reduced ? undefined : 'opacity 250ms ease-out' }}
            />
          )}

          {/* Main reaction path */}
          <path
            d={mainPath}
            fill="none"
            stroke="var(--ws-accent)"
            strokeWidth="2.5"
            strokeLinejoin="round"
            strokeLinecap="round"
            style={{ transition: reduced ? undefined : 'opacity 250ms ease-out' }}
          />

          {/* Ea bracket (reactants → peak) */}
          <g opacity={dimmed(focus.ea)} style={{ transition: reduced ? undefined : 'opacity 250ms ease-out' }}>
            <line x1={eaX} y1={reactPt.y} x2={eaX} y2={peakPt.y} stroke="var(--ws-accent-hover)" strokeWidth="1.25" markerStart="url(#ep-arrow)" markerEnd="url(#ep-arrow)" />
            <rect x={eaX + 3} y={(reactPt.y + peakPt.y) / 2 - 7} width="34" height="14" rx="3" fill="var(--ws-accent-quiet)" />
            <text x={eaX + 20} y={(reactPt.y + peakPt.y) / 2 + 3} textAnchor="middle" fontSize="8.5" fontWeight="600" fill="var(--ws-accent-ink)">
              Ea
            </text>
          </g>

          {/* Catalysed Ea bracket */}
          {showCatalyst && (
            <g opacity={dimmed(focus.catalyst)} style={{ transition: reduced ? undefined : 'opacity 250ms ease-out' }}>
              <line x1={eaX - 40} y1={reactPt.y} x2={eaX - 40} y2={catPeakPt.y} stroke="var(--ws-success)" strokeWidth="1" strokeDasharray="2 2" />
              <text x={eaX - 44} y={(reactPt.y + catPeakPt.y) / 2 + 3} textAnchor="end" fontSize="7.5" fontWeight="600" fill="var(--ws-success)">
                Ea (cat.)
              </text>
            </g>
          )}

          {/* ΔH bracket (reactants → products) */}
          <g opacity={dimmed(focus.deltaH)} style={{ transition: reduced ? undefined : 'opacity 250ms ease-out' }}>
            <line x1={dhX} y1={reactPt.y} x2={dhX} y2={prodPt.y} stroke={exo ? 'var(--ws-success)' : 'var(--ws-danger)'} strokeWidth="1.25" markerStart="url(#ep-arrow)" markerEnd="url(#ep-arrow)" />
            <rect x={dhX - 18} y={(reactPt.y + prodPt.y) / 2 - 7} width="20" height="14" rx="3" fill={exo ? 'var(--ws-success)' : 'var(--ws-danger)'} />
            <text x={dhX - 8} y={(reactPt.y + prodPt.y) / 2 + 3} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#FFFFFF">
              ΔH
            </text>
          </g>

          {/* Transition-state marker at the peak */}
          <g opacity={dimmed(focus.activation)} style={{ transition: reduced ? undefined : 'opacity 250ms ease-out' }}>
            <circle cx={peakPt.x} cy={peakPt.y} r={focus.activation ? 4.5 : 3.5} fill="var(--ws-accent)" stroke="var(--ws-surface)" strokeWidth="1.5" />
            <text x={peakPt.x} y={peakPt.y - 7} textAnchor="middle" fontSize="7.5" fill="var(--ws-ink-3)">
              transition state
            </text>
          </g>

          {/* Reactants endpoint + label */}
          <g opacity={dimmed(focus.reactants)} style={{ transition: reduced ? undefined : 'opacity 250ms ease-out' }}>
            <circle cx={reactPt.x} cy={reactPt.y} r={focus.reactants ? 4 : 3} fill="var(--ws-ink-2)" />
            <text x={reactPt.x - 2} y={reactPt.y - 7} textAnchor="middle" fontSize="8" fontWeight="600" fill="var(--ws-ink)">
              {reactantLabel}
            </text>
          </g>

          {/* Products endpoint + label */}
          <g opacity={dimmed(focus.products)} style={{ transition: reduced ? undefined : 'opacity 250ms ease-out' }}>
            <circle cx={prodPt.x} cy={prodPt.y} r={focus.products ? 4 : 3} fill="var(--ws-ink-2)" />
            <text x={prodPt.x} y={prodPt.y + (exo ? 13 : -7)} textAnchor="middle" fontSize="8" fontWeight="600" fill="var(--ws-ink)">
              {productLabel}
            </text>
          </g>

          {/* Cue.progress marker riding the curve */}
          {markerPt && (
            <circle cx={markerPt.x} cy={markerPt.y} r="4.5" fill="var(--ws-accent-hover)" stroke="var(--ws-surface)" strokeWidth="1.5" />
          )}

          {/* Arrowhead marker for the energy brackets */}
          <defs>
            <marker id="ep-arrow" viewBox="0 0 8 8" refX="4" refY="4" markerWidth="5" markerHeight="5" orient="auto">
              <path d="M1 1 L7 4 L1 7 Z" fill="context-stroke" />
            </marker>
          </defs>
        </svg>

        {/* Stage stepper — clickable, mirrors cue.step */}
        <div className="mt-3 grid grid-cols-3 gap-1.5">
          {STEP_ORDER.map((k, i) => {
            const active = step === i;
            const labels: Record<StepKey, string> = {
              reactants: '1 · Reactants',
              activation: '2 · Activation',
              products: '3 · Products',
            };
            return (
              <button
                key={k}
                type="button"
                onClick={() => {
                  cancelDemo();
                  setStep(i);
                  setMarker(null);
                  mark();
                }}
                className={`rounded-sm border px-2 py-1.5 text-[11px] font-medium transition-colors ${
                  active
                    ? 'border-ws-accent bg-ws-accent text-white'
                    : 'border-ws-border bg-ws-tile text-ws-ink-2 hover:bg-ws-surface-2'
                }`}
              >
                {labels[k]}
              </button>
            );
          })}
        </div>

        {/* Live numeric readout */}
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
          <Stat label="Activation energy" value={`${ea.toFixed(0)} kJ/mol`} sub="Ea (reactants → peak)" />
          <Stat
            label="Enthalpy change"
            value={`${deltaH > 0 ? '+' : ''}${deltaH.toFixed(0)} kJ/mol`}
            sub={exo ? 'ΔH < 0 · released' : 'ΔH > 0 · absorbed'}
            accent={exo ? 'var(--ws-success)' : 'var(--ws-danger)'}
          />
          <Stat
            label="Reaction type"
            value={exo ? 'Exothermic' : 'Endothermic'}
            sub={showCatalyst ? `catalyst lowers Ea → ${catEa.toFixed(0)}` : undefined}
          />
        </div>

        <p className="mt-2 min-h-[2rem] text-xs text-ws-ink-3">{stepNote[step] ?? stepNote[-1]}</p>
      </div>
    </VizCard>
  );
}

function Stat({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: string;
}) {
  return (
    <div className="rounded-md border border-ws-border bg-ws-tile px-2.5 py-1.5">
      <div className="text-[10px] uppercase tracking-wide text-ws-ink-3">{label}</div>
      <div className="font-mono text-sm font-semibold tabular-nums" style={{ color: accent ?? 'var(--ws-ink)' }}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-ws-ink-3">{sub}</div>}
    </div>
  );
}
